export { default as DatePicker } from "./DatePicker.vue";
export { default as DateRangePicker } from "./DateRangePicker.vue";
export { default as TimeAgo } from "./TimeAgo.vue";

import { addDays, endOfDay, startOfDay, subDays, subYears } from "date-fns";

import { useI18n } from "vue-i18n";

export const getShortcutName = (shortcut) => {
    const { t } = useI18n();
    return t("components.inputs.datepicker.shortcuts." + shortcut);
};

export const DateShortcut = Object.freeze({
    TODAY: "today",
    YESTERDAY: "yesterday",
    TOMORROW: "tomorrow",
    NEXT_7_DAYS: "next_7_days",
    NEXT_30_DAYS: "next_30_days",
    NEXT_12_MONTHS: "next_12_months",
    LAST_7_DAYS: "last_7_days",
    LAST_30_DAYS: "last_30_days",
    LAST_12_MONTHS: "last_12_months",
    CUSTOM: "custom",
    OVERDUE: "overdue",
    EXPIRED: "expired",
    NO_DUE_DATE: "no_due_date",
    NO_COMPLETED_DATE: "no_completed_date",
    NO_NOTIFICATION_DATE: "no_notification_date",
    NO_EXPIRATION_DATE: "no_expiration_date",
});

export const useDateShortcuts = () => {
    const createdDateShortcuts = [
        {
            id: DateShortcut.TODAY,
            name: getShortcutName(DateShortcut.TODAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.YESTERDAY,
            name: getShortcutName(DateShortcut.YESTERDAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 1)),
                    end: endOfDay(subDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.LAST_7_DAYS,
            name: getShortcutName(DateShortcut.LAST_7_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 7)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.LAST_30_DAYS,
            name: getShortcutName(DateShortcut.LAST_30_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 30)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.LAST_12_MONTHS,
            name: getShortcutName(DateShortcut.LAST_12_MONTHS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subYears(date, 1)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.CUSTOM,
            name: getShortcutName(DateShortcut.CUSTOM),
            range: () => {},
        },
    ];

    const dueDateShortcuts = [
        {
            id: DateShortcut.NO_DUE_DATE,
            name: getShortcutName(DateShortcut.NO_DUE_DATE),
            range: () => {
                return {
                    start: null,
                    end: null,
                };
            },
        },
        {
            id: DateShortcut.OVERDUE,
            name: getShortcutName(DateShortcut.OVERDUE),
            range: () => {
                const date = new Date();

                return {
                    start: null,
                    end: endOfDay(subDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.TODAY,
            name: getShortcutName(DateShortcut.TODAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.TOMORROW,
            name: getShortcutName(DateShortcut.TOMORROW),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(addDays(date, 1)),
                    end: endOfDay(addDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.NEXT_7_DAYS,
            name: getShortcutName(DateShortcut.NEXT_7_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(addDays(date, 7)),
                };
            },
        },
        {
            id: DateShortcut.NEXT_30_DAYS,
            name: getShortcutName(DateShortcut.NEXT_30_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(addDays(date, 30)),
                };
            },
        },
        {
            id: DateShortcut.CUSTOM,
            name: getShortcutName(DateShortcut.CUSTOM),
            range: () => {},
        },
    ];

    const expirationDateShortcuts = [
        {
            id: DateShortcut.NO_EXPIRATION_DATE,
            name: getShortcutName(DateShortcut.NO_EXPIRATION_DATE),
            range: () => {
                return {
                    start: null,
                    end: null,
                };
            },
        },
        {
            id: DateShortcut.EXPIRED,
            name: getShortcutName(DateShortcut.EXPIRED),
            range: () => {
                const date = new Date();

                return {
                    start: null,
                    end: endOfDay(subDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.TODAY,
            name: getShortcutName(DateShortcut.TODAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.TOMORROW,
            name: getShortcutName(DateShortcut.TOMORROW),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(addDays(date, 1)),
                    end: endOfDay(addDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.NEXT_7_DAYS,
            name: getShortcutName(DateShortcut.NEXT_7_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(addDays(date, 7)),
                };
            },
        },
        {
            id: DateShortcut.NEXT_30_DAYS,
            name: getShortcutName(DateShortcut.NEXT_30_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(addDays(date, 30)),
                };
            },
        },
        {
            id: DateShortcut.CUSTOM,
            name: getShortcutName(DateShortcut.CUSTOM),
            range: () => {},
        },
    ];

    const notificationDateShortcuts = [
        {
            id: DateShortcut.NO_NOTIFICATION_DATE,
            name: getShortcutName(DateShortcut.NO_NOTIFICATION_DATE),
            range: () => {
                return {
                    start: null,
                    end: null,
                };
            },
        },
        {
            id: DateShortcut.TODAY,
            name: getShortcutName(DateShortcut.TODAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.TOMORROW,
            name: getShortcutName(DateShortcut.TOMORROW),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(addDays(date, 1)),
                    end: endOfDay(addDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.NEXT_7_DAYS,
            name: getShortcutName(DateShortcut.NEXT_7_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(addDays(date, 7)),
                };
            },
        },
        {
            id: DateShortcut.NEXT_30_DAYS,
            name: getShortcutName(DateShortcut.NEXT_30_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(addDays(date, 30)),
                };
            },
        },
        {
            id: DateShortcut.CUSTOM,
            name: getShortcutName(DateShortcut.CUSTOM),
            range: () => {},
        },
    ];

    const completedDateShortcuts = [
        {
            id: DateShortcut.NO_COMPLETED_DATE,
            name: getShortcutName(DateShortcut.NO_COMPLETED_DATE),
            range: () => {
                return {
                    start: null,
                    end: null,
                };
            },
        },
        {
            id: DateShortcut.TODAY,
            name: getShortcutName(DateShortcut.TODAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.YESTERDAY,
            name: getShortcutName(DateShortcut.YESTERDAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 1)),
                    end: endOfDay(subDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.LAST_7_DAYS,
            name: getShortcutName(DateShortcut.LAST_7_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 7)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.LAST_30_DAYS,
            name: getShortcutName(DateShortcut.LAST_30_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 30)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.LAST_12_MONTHS,
            name: getShortcutName(DateShortcut.LAST_12_MONTHS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subYears(date, 1)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.CUSTOM,
            name: getShortcutName(DateShortcut.CUSTOM),
            range: () => {},
        },
    ];

    const purchasedDateShortcuts = [
        {
            id: DateShortcut.TODAY,
            name: getShortcutName(DateShortcut.TODAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(date),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.YESTERDAY,
            name: getShortcutName(DateShortcut.YESTERDAY),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 1)),
                    end: endOfDay(subDays(date, 1)),
                };
            },
        },
        {
            id: DateShortcut.LAST_7_DAYS,
            name: getShortcutName(DateShortcut.LAST_7_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 7)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.LAST_30_DAYS,
            name: getShortcutName(DateShortcut.LAST_30_DAYS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subDays(date, 30)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.LAST_12_MONTHS,
            name: getShortcutName(DateShortcut.LAST_12_MONTHS),
            range: () => {
                const date = new Date();

                return {
                    start: startOfDay(subYears(date, 1)),
                    end: endOfDay(date),
                };
            },
        },
        {
            id: DateShortcut.CUSTOM,
            name: getShortcutName(DateShortcut.CUSTOM),
            range: () => {},
        },
    ];

    return {
        createdDateShortcuts,
        dueDateShortcuts,
        expirationDateShortcuts,
        notificationDateShortcuts,
        completedDateShortcuts,
        purchasedDateShortcuts,
    };
};
